import React, {Component, useCallback, useContext, useEffect, useRef, useState} from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import styled from 'styled-components';
import {disableBodyScroll, enableBodyScroll} from 'body-scroll-lock';

import {Button} from '../../atoms/Button';
import vars from '../../utils/vars';
import {maxMediaQueries} from '../../utils/styling';
import {GlobalContext} from '../../context/GlobalContext';
import {H3} from '../../atoms/TextComponents';

const ModalWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 32px 24px;
  background: rgba(150, 161, 169, 0.51);
  z-index: 1011;
`;

const ModalContent = styled.div`
  flex: 0 0 auto;
  background-color: #ffffff;
  padding: 20px;
  width: 700px;
  box-shadow: 0 0 1px rgba(0, 0, 0, 0.04), 0 2px 6px rgba(0, 0, 0, 0.04), 0 16px 24px rgba(0, 0, 0, 0.06);
  border-radius: 4px;
  
  ${maxMediaQueries.sm`
    width: 100%;
  `}
  
  h3 {
    margin-bottom: 8px;
  }
  
  button {
    margin: 24px 0 0 0;
  }
  
  form {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
  }
`;

const Field = styled.div`
  width: calc(50% - 10px);
  position: relative;
  margin-top: 24px;
  
  ${maxMediaQueries.lg`
    width: 100%;
  `}
  
  label {
    display: block;
    font-size: 14px;
    line-height: 16px;
    color: ${({theme}) => theme.colors.gray900};
    margin-bottom: 8px;
  }
  input,
  select,
  textarea {
    display: block;
    width: 100%;
    line-height: 16px;
    padding: 11px 8px;
    color: ${({theme}) => theme.colors.gray600};
    background-color: ${({theme}) => theme.colors.gray100};
    border: 1px solid ${({theme}) => theme.colors.gray200};
    border-radius: 4px;
    font-size: 14px;
    line-height: 16px;
    -webkit-appearance: none;
    -moz-appearance: none;
   
    &:focus,
    &:active {
      outline: none;
    }
  }
  
  select {
    background-image: url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iOCIgaGVpZ2h0PSI0IiB2aWV3Qm94PSIwIDAgOCA0IiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxwYXRoIGQ9Ik04IDBMNCA0TDMuNDk2OTFlLTA3IC02Ljk5MzgyZS0wN0w4IDBaIiBmaWxsPSIjODI4ODk0Ii8+PC9zdmc+),linear-gradient(to bottom, ${({theme}) => theme.colors.gray100} 0%,${({theme}) => theme.colors.gray100} 100%);
    background-repeat: no-repeat, repeat;
    background-position: right 12px top 18px, 0 0;
    background-size: 8px auto, 100%;
    
    &::-ms-expand {
      display: none;
    }
    
    option {
      font-weight:normal;
    }
  }
       
  textarea {
    width: 100%;
  }
`;

const Error = styled.p`
  line-height: 1.4;
  font-size: 14px;
  margin: 10px 0;
  color: maroon;
`;

const ContactForm = ({isVisible}) => {
  const modalRef = useRef(null);
  const {closeContactForm: close} = useContext(GlobalContext);
  const [step, setStep] = useState('input');
  const [data, setData] = useState({
    firstName: null,
    lastName: null,
    email: null,
    supportTeamSize: 'Less than 20 agents',
    comment: ''
  });

  const updateField = useCallback((e, field) => {
    setData({
      ...data,
      [field]: e.target.value
    });
  }, [data]);

  const submitForm = useCallback((e) => {
    e.preventDefault();

    const params = {
      'fields': [
        {
          'name': 'firstname',
          'value': data.firstName
        },
        {
          'name': 'lastname',
          'value': data.lastName
        },
        {
          'name': 'email',
          'value': data.email
        },
        {
          'name': 'support_team_size',
          'value': data.supportTeamSize
        },
        {
          'name': 'leave_a_comment',
          'value': data.comment
        }
      ]
      // "context": {
      //   "hutk": ':hutk', // include this parameter and set it to the hubspotutk cookie value to enable cookie tracking on your submission
      //   "pageUri": "www.example.com/page",
      //   "pageName": "Example page"
      // },
      // "legalConsentOptions":{ // Include this object when GDPR options are enabled
      //   "consent":{
      //     "consentToProcess":true,
      //     "text":"I agree to allow Example Company to store and process my personal data.",
      //     "communications":[
      //       {
      //         "value":true,
      //         "subscriptionTypeId":999,
      //         "text":"I agree to receive marketing communications from Example Company."
      //       }
      //     ]
      //   }
      // }
    };

    axios.post(
        `https://api.hsforms.com/submissions/v3/integration/submit/${vars.hubspot.portal_id}/${vars.hubspot.form_id}`,
        params,
        {headers: {'Content-Type': 'application/json'}}
    ).then(() => {
      setStep('submitted');
    }).catch(e => {
      const {errors} = e.response.data;
      if (!!errors.find(i => i.errorType == 'BLOCKED_EMAIL')) {
        setStep('blocked-email');
      }
      else {
        setStep('error');
      }
    });
  }, [data]);

  useEffect(() => {
    if (modalRef.current) {
      disableBodyScroll();
    } else {
      enableBodyScroll();
    }
  }, [isVisible]);

  if (isVisible) {

    let content;

    if (step === 'input') {
      content = (
          <ModalContent ref={modalRef} onClick={e => {
            e.stopPropagation();
          }}>
            <H3 style={{textAlign: 'center'}}>Learn more about Miuros</H3>
            <form id="contact-form" onSubmit={submitForm}>
              <Field>
                <label htmlFor="firstname">First Name</label>
                <input
                    placeholder="Enter your first name"
                    type="text"
                    id="firstname"
                    name="firstname"
                    required
                    onChange={e => updateField(e, 'firstName')}
                />
              </Field>
              <Field>
                <label htmlFor="lastname">Last Name</label>
                <input
                    placeholder="Enter your last name"
                    type="text"
                    id="lastname"
                    name="lastname"
                    required
                    onChange={e => updateField(e, 'lastName')}
                />
              </Field>
              <Field>
                <label htmlFor="email">Email</label>
                <input
                    type="email"
                    placeholder="Enter your email"
                    id="email"
                    name="email"
                    required
                    onChange={e => updateField(e, 'email')}
                />
              </Field>
              <Field>
                <label htmlFor="team_size">
                  Customer service team size
                </label>
                <select
                    type="email"
                    id="team_size"
                    name="team_size"
                    required
                    onChange={e => updateField(e, 'supportTeamSize')}
                >
                  <option value="Less than 20 agents">
                    Less than 20 agents
                  </option>
                  <option value="Between 20 and 49 agents">
                    Between 20 and 49 agents
                  </option>
                  <option value="Between 50 and 99 agents">
                    Between 50 and 99 agents
                  </option>
                  <option value="Between 100 and 199 agents">
                    Between 100 and 199 agents
                  </option>
                  <option value="More than 199 agents">
                    More than 199 agents
                  </option>
                </select>
              </Field>
              <Field style={{width: '100%', maxWidth: '100%'}}>
                <label htmlFor="team_size">
                  Leave a comment
                </label>
                <textarea onChange={e => updateField(e, 'comment')}></textarea>
              </Field>
              <Button primary type="submit" fluid>Book a Demo</Button>
            </form>
          </ModalContent>
      );
    } else if (step === 'submitted') {

      content = (
          <ModalContent ref={modalRef} onClick={e => {
            e.stopPropagation();
          }}>
            <H3 style={{textAlign: 'center'}}>
              Thanks for submitting the form. We will get back to you shortly
            </H3>
          </ModalContent>
      );
    } else if (step === 'blocked-email') {
      content = (
          <ModalContent ref={modalRef} onClick={e => {
            e.stopPropagation();
          }}>
            <Error>
              Submission from this email address are not allowed
            </Error>
          </ModalContent>
      );
    } else if (step === 'error') {
      content = (
          <ModalContent ref={modalRef} onClick={e => {
            e.stopPropagation();
          }}>
            <H3 style={{textAlign: 'center'}}>Your message can't be sent now</H3>
            <p>Please try again later</p>
          </ModalContent>
      );
    }

    return ReactDOM.createPortal(
        <ModalWrapper onClick={close}>{content}</ModalWrapper>,
        document.querySelector('body')
    );
  } else {
    return null;
  }

};

export default ContactForm;
